import type { GeoItem, TractionStats } from '@/hooks/useSuperStats';

interface TractionPanelProps {
  traction: TractionStats;
  countries: GeoItem[];
  loading: boolean;
}

function flagOf(code: string | null): string {
  if (!code || code.trim().length !== 2) return '';
  const base = 0x1f1e6;
  const upper = code.trim().toUpperCase();
  const first = upper.charCodeAt(0) - 65;
  const second = upper.charCodeAt(1) - 65;
  if (first < 0 || first > 25 || second < 0 || second > 25) return '';
  return String.fromCodePoint(base + first, base + second);
}

function GrowthTile({
  label,
  value,
  delta,
  icon,
}: {
  label: string;
  value: string;
  delta: number | null;
  icon: string;
}) {
  const hasDelta = delta != null && Number.isFinite(delta);
  const positive = (delta ?? 0) >= 0;
  return (
    <div className="rounded-lg bg-background-50/10 border border-background-50/20 p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[11px] font-medium text-background-50/75">{label}</span>
        <i className={`${icon} text-background-50/70`}></i>
      </div>
      <div className="text-2xl font-bold font-heading text-background-50">{value}</div>
      {hasDelta && (
        <span
          className={`inline-flex items-center gap-0.5 text-[11px] font-semibold rounded-full px-1.5 py-0.5 mt-1.5 whitespace-nowrap ${
            positive ? 'bg-accent-500/25 text-accent-100' : 'bg-red-500/25 text-red-100'
          }`}
        >
          <i className={positive ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
          {positive ? '+' : ''}
          {(delta as number).toFixed(1)}%
        </span>
      )}
    </div>
  );
}

/**
 * Bloc « Traction » mis en avant en tête de page pour les investisseurs :
 * croissance 7 j / 30 j, principaux pays et taux de retour des visiteurs.
 */
export default function TractionPanel({ traction, countries, loading }: TractionPanelProps) {
  const topCountries = countries.slice(0, 3);
  const returning = traction.returningRate != null ? `${traction.returningRate.toFixed(0)}%` : '—';

  return (
    <section className="rounded-lg border border-background-200/70 bg-gradient-to-br from-foreground-950 via-foreground-900 to-primary-800 text-background-50 p-5 md:p-6 mb-5 md:mb-6">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-8 h-8 rounded-lg bg-accent-500 text-background-50 flex items-center justify-center">
          <i className="ri-rocket-2-line"></i>
        </span>
        <div>
          <h3 className="text-base font-bold font-heading text-background-50">Traction</h3>
          <p className="text-[11px] text-background-50/70">
            Visites publiques Zifek — 60 derniers jours
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <i className="ri-loader-4-line animate-spin text-2xl text-background-50/80"></i>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <GrowthTile
            label="Croissance 7 jours"
            value={traction.views7d.toLocaleString('fr-FR')}
            delta={traction.growth7d}
            icon="ri-arrow-up-circle-line"
          />
          <GrowthTile
            label="Croissance 30 jours"
            value={traction.views30d.toLocaleString('fr-FR')}
            delta={traction.growth30d}
            icon="ri-line-chart-line"
          />
          <div className="rounded-lg bg-background-50/10 border border-background-50/20 p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-[11px] font-medium text-background-50/75">Principaux pays</span>
              <i className="ri-earth-line text-background-50/70"></i>
            </div>
            {topCountries.length === 0 ? (
              <div className="text-sm text-background-50/60">Aucune donnée géo</div>
            ) : (
              <div className="space-y-1">
                {topCountries.map((country) => {
                  const flag = flagOf(country.code);
                  return (
                    <div key={country.name} className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate text-background-50/90">
                        {flag || '🌍'} {country.name}
                      </span>
                      <span className="font-semibold text-background-50 whitespace-nowrap">
                        {country.value.toLocaleString('fr-FR')}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="rounded-lg bg-background-50/10 border border-background-50/20 p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-[11px] font-medium text-background-50/75">
                Taux de retour visiteurs
              </span>
              <i className="ri-repeat-2-line text-background-50/70"></i>
            </div>
            <div className="text-2xl font-bold font-heading text-background-50">{returning}</div>
            <div className="text-[11px] text-background-50/60 mt-1">
              {traction.returningVisitors.toLocaleString('fr-FR')} retour(s) sur{' '}
              {traction.totalVisitors.toLocaleString('fr-FR')} visiteur(s)
            </div>
          </div>
        </div>
      )}
    </section>
  );
}